<script lang="ts">
    import { onMount } from 'svelte';
    import { get } from 'svelte/store';
    import { push } from 'svelte-spa-router';
    import { api } from '../../framework/js/api.js';
    import { toast } from '../../framework/js/toast.js';
    import { spinner } from '../../framework/js/spinner.js';
    import { StatusBadge } from '../../framework/components/app';
    import { userRoleTone, userStatusTone } from './adminListBadges.js';

    interface UserRow {
        uuid: string;
        email: string;
        salutation?: string;
        first_name: string;
        last_name: string;
        phone?: string;
        team_count?: number;
        user_role?: { uuid: string; name: string } | null;
        user_status?: { uuid: string; name: string } | null;
        active_team_uuid?: string | null;
        created_at?: string;
    }

    interface Paging {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from: number | null;
        to: number | null;
    }

    interface ListOption {
        uuid: string;
        name: string;
    }

    const DEFAULT_PER_PAGE = 20;
    const PER_PAGE_OPTIONS = [20, 50, 100];

    const SORT_API_COLUMNS: Record<string, string> = {
        email: 'email',
        salutation: 'salutation',
        first_name: 'first_name',
        last_name: 'last_name',
        phone: 'phone',
        team_count: 'team_count',
        role: 'role_name',
        status: 'status_name',
        created_at: 'created_at',
    };

    let users: UserRow[] = [];
    let isLoading = false;
    let searchQuery = '';
    let roleFilter = '';
    let statusFilter = '';
    let sortField = 'last_name';
    let sortDirection: 'asc' | 'desc' = 'asc';
    let currentPage = 1;
    let perPage = DEFAULT_PER_PAGE;
    let roleOptions: ListOption[] = [];
    let statusOptions: ListOption[] = [];
    let paging: Paging = {
        current_page: 0,
        last_page: 1,
        per_page: DEFAULT_PER_PAGE,
        total: 0,
        from: null,
        to: null,
    };

    onMount(async () => {
        if (!get(api).lists) {
            await api.getResourceLists();
        }
        const lists = get(api).lists as Record<string, ListOption[]> | null;
        roleOptions = lists?.user_roles ?? [];
        statusOptions = lists?.user_statuses ?? [];
        await loadUsers();
    });

    async function loadUsers() {
        isLoading = true;
        spinner.show();
        try {
            const params: Record<string, string | number> = {
                page: currentPage,
                per_page: Number(perPage) || DEFAULT_PER_PAGE,
                column: SORT_API_COLUMNS[sortField] ?? sortField,
                order: sortDirection,
            };
            if (searchQuery.trim()) params.phrase = searchQuery.trim();
            if (roleFilter) params.user_role_uuid = roleFilter;
            if (statusFilter) params.user_status_uuid = statusFilter;

            const response = await api.crudIndex('user', params);
            if (response.success) {
                users = (response.data as UserRow[]) ?? [];
                paging = (response.paging as Paging) ?? paging;
            } else {
                toast.showNotification(false, response.message || 'Failed to load users');
                users = [];
            }
        } catch (error) {
            toast.showNotification(false, 'Error loading users');
            console.error(error);
            users = [];
        } finally {
            spinner.hide();
            isLoading = false;
        }
    }

    function handleSort(field: string) {
        if (sortField === field) {
            sortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            sortField = field;
            sortDirection = field === 'last_name' || field === 'first_name' || field === 'email' ? 'asc' : 'desc';
        }
        currentPage = 1;
        loadUsers();
    }

    function goToPage(page: number) {
        if (page < 1 || page > paging.last_page) return;
        currentPage = page;
        loadUsers();
    }

    function viewUser(userUuid: string) {
        push(`/admin/users/${userUuid}`);
    }

    function handleRowKeydown(event: KeyboardEvent, userUuid: string) {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            viewUser(userUuid);
        }
    }

    function formatDate(dateString?: string): string {
        if (!dateString) return '—';
        return new Date(dateString).toLocaleDateString(navigator.language, {
            dateStyle: 'medium',
        });
    }

    function fullName(user: UserRow): string {
        return [user.first_name, user.last_name].filter(Boolean).join(' ') || '—';
    }

    let searchDebounceTimer: ReturnType<typeof setTimeout>;
    function debounceSearch() {
        clearTimeout(searchDebounceTimer);
        searchDebounceTimer = setTimeout(() => {
            currentPage = 1;
            loadUsers();
        }, 300);
    }

    function onFilterChange() {
        currentPage = 1;
        loadUsers();
    }

    function onPerPageChange() {
        currentPage = 1;
        loadUsers();
    }

    $: displayPage = (paging.current_page ?? 0) + 1;
</script>

<div class="page-shell admin-list">
    <div class="page-header">
        <div class="header-content">
            <h2 class="page-title">Users</h2>
            <p class="page-subtitle">View all users and their team memberships</p>
        </div>
        <div class="header-stats">
            <span class="stat-badge">
                {#if paging.total > 0}
                    Showing {paging.from ?? 0}–{paging.to ?? 0} of {paging.total} user{paging.total !== 1 ? 's' : ''}
                {:else}
                    0 users
                {/if}
            </span>
        </div>
    </div>

    <div class="filters-bar">
        <div class="filters-group">
            <div class="search-box">
                <input
                    type="text"
                    placeholder="Search by name or email..."
                    bind:value={searchQuery}
                    on:input={debounceSearch}
                    class="search-input"
                    aria-label="Search users"
                />
            </div>
            <div class="filter-select">
                <label for="admin-user-role-filter" class="sr-only">Filter by role</label>
                <select id="admin-user-role-filter" bind:value={roleFilter} on:change={onFilterChange}>
                    <option value="">All Roles</option>
                    {#each roleOptions as option (option.uuid)}
                        <option value={option.uuid}>{option.name}</option>
                    {/each}
                </select>
            </div>
            <div class="filter-select">
                <label for="admin-user-status-filter" class="sr-only">Filter by status</label>
                <select id="admin-user-status-filter" bind:value={statusFilter} on:change={onFilterChange}>
                    <option value="">All Statuses</option>
                    {#each statusOptions as option (option.uuid)}
                        <option value={option.uuid}>{option.name}</option>
                    {/each}
                </select>
            </div>
        </div>
        <div class="per-page-select">
            <label for="admin-user-per-page">Per page</label>
            <select id="admin-user-per-page" bind:value={perPage} on:change={onPerPageChange}>
                {#each PER_PAGE_OPTIONS as n}
                    <option value={n}>{n}</option>
                {/each}
            </select>
        </div>
        <span class="results-count">
            {paging.total} {paging.total === 1 ? 'user' : 'users'}
        </span>
    </div>

    {#if isLoading}
        <div class="loading-state">Loading users...</div>
    {:else if users.length === 0}
        <div class="empty-state">
            <i class="users icon empty-icon" aria-hidden="true"></i>
            <p>No users found.</p>
            {#if searchQuery || roleFilter || statusFilter}
                <button
                    type="button"
                    class="clear-filters-btn"
                    on:click={() => { searchQuery = ''; roleFilter = ''; statusFilter = ''; currentPage = 1; loadUsers(); }}
                >
                    Clear filters
                </button>
            {/if}
        </div>
    {:else}
        <div class="table-wrap">
            <table class="data-table">
                <thead>
                    <tr>
                        <th class="sortable" on:click={() => handleSort('email')}>
                            <i class="mail icon" aria-hidden="true"></i> Email
                            {#if sortField === 'email'}
                                <i class="icon {sortDirection === 'asc' ? 'arrow up' : 'arrow down'}" aria-hidden="true"></i>
                            {/if}
                        </th>
                        <th class="sortable" on:click={() => handleSort('first_name')}>
                            <i class="user icon" aria-hidden="true"></i> Name
                            {#if sortField === 'first_name'}
                                <i class="icon {sortDirection === 'asc' ? 'arrow up' : 'arrow down'}" aria-hidden="true"></i>
                            {/if}
                        </th>
                        <th class="sortable" on:click={() => handleSort('phone')}>
                            <i class="phone icon" aria-hidden="true"></i> Phone
                            {#if sortField === 'phone'}
                                <i class="icon {sortDirection === 'asc' ? 'arrow up' : 'arrow down'}" aria-hidden="true"></i>
                            {/if}
                        </th>
                        <th class="sortable center-col num-col" on:click={() => handleSort('team_count')}>
                            <i class="users icon" aria-hidden="true"></i> Teams
                            {#if sortField === 'team_count'}
                                <i class="icon {sortDirection === 'asc' ? 'arrow up' : 'arrow down'}" aria-hidden="true"></i>
                            {/if}
                        </th>
                        <th class="sortable" on:click={() => handleSort('role')}>
                            <i class="id badge icon" aria-hidden="true"></i> Role
                            {#if sortField === 'role'}
                                <i class="icon {sortDirection === 'asc' ? 'arrow up' : 'arrow down'}" aria-hidden="true"></i>
                            {/if}
                        </th>
                        <th class="sortable" on:click={() => handleSort('status')}>
                            <i class="signal icon" aria-hidden="true"></i> Status
                            {#if sortField === 'status'}
                                <i class="icon {sortDirection === 'asc' ? 'arrow up' : 'arrow down'}" aria-hidden="true"></i>
                            {/if}
                        </th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {#each users as user (user.uuid)}
                        <tr
                            class="clickable-row data-row"
                            tabindex="0"
                            on:click={() => viewUser(user.uuid)}
                            on:keydown={(e) => handleRowKeydown(e, user.uuid)}
                        >
                            <td class="email-cell">{user.email}</td>
                            <td class="name-cell">
                                <div class="user-name">{fullName(user)}</div>
                                {#if user.salutation}
                                    <div class="user-meta">{user.salutation}</div>
                                {/if}
                            </td>
                            <td>{user.phone || '—'}</td>
                            <td class="center-col num-col">{user.team_count ?? 0}</td>
                            <td>
                                <StatusBadge
                                    tone={userRoleTone(user.user_role?.name)}
                                    label={user.user_role?.name ?? '—'}
                                />
                            </td>
                            <td>
                                <StatusBadge
                                    tone={userStatusTone(user.user_status?.name)}
                                    label={user.user_status?.name ?? '—'}
                                />
                            </td>
                            <td class="actions-cell">
                                <button
                                    type="button"
                                    class="view-button"
                                    on:click|stopPropagation={() => viewUser(user.uuid)}
                                >
                                    <i class="icon eye" aria-hidden="true"></i>
                                    View
                                </button>
                            </td>
                        </tr>
                    {/each}
                </tbody>
            </table>
        </div>

        {#if paging.last_page > 1}
            <div class="pagination">
                <span class="pagination-info">
                    Showing {paging.from ?? 0}–{paging.to ?? 0} of {paging.total}
                </span>
                <div class="pagination-buttons">
                    <button type="button" class="pagination-btn" disabled={displayPage <= 1} on:click={() => goToPage(displayPage - 1)} aria-label="Previous page">
                        Previous
                    </button>
                    <span class="page-indicator">Page {displayPage} of {paging.last_page}</span>
                    <button type="button" class="pagination-btn" disabled={displayPage >= paging.last_page} on:click={() => goToPage(displayPage + 1)} aria-label="Next page">
                        Next
                    </button>
                </div>
            </div>
        {/if}
    {/if}
</div>

